import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Card } from '@orisini/ui/components/card'
import { accountsApi, type Overview } from '../lib/accounts'

/**
 * /app — Dashboard: angka ringkas seluruh Orisini. Daftar project pindah ke
 * /projects; di sini hanya totalnya, masing-masing menaut ke halaman yang
 * memuat isinya.
 */
export default function DashboardPage() {
  const [data, setData] = useState<Overview | null>(null)

  useEffect(() => {
    accountsApi
      .overview()
      .then(setData)
      .catch((err) => toast.error(err instanceof Error ? err.message : "Couldn't load the dashboard"))
  }, [])

  if (!data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      </div>
    )
  }

  const dibuat = data.posters + data.carousels + data.videos

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          {dibuat} {dibuat === 1 ? 'piece' : 'pieces'} of content made across {data.projects}{' '}
          {data.projects === 1 ? 'project' : 'projects'}.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat
          label="Projects"
          value={data.projects}
          hint={`${data.projectsWithAccount} connected to an account`}
          to="/projects"
        />
        <Stat
          label="Connected accounts"
          value={data.accounts}
          hint={
            data.accountsPartial
              ? 'Some platforms are unavailable right now'
              : data.accountLimit
                ? `${data.accountLimit.max} on your plan`
                : undefined
          }
          to="/projects"
        />
        <Stat label="Published" value={data.published} hint={`${data.queued} in the queue`} to="/bank?status=published" />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Content made</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Posters" value={data.posters} to="/bank" />
          <Stat label="Carousels" value={data.carousels} to="/bank" />
          <Stat label="Videos" value={data.videos} to="/bank" />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">In progress</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Stat label="Being made" value={data.inProgress} hint="Drafting, approved or rendering" to="/bank?status=working" />
          <Stat label="Waiting for review" value={data.awaitingReview} to="/review" />
        </div>
      </section>

      {data.projects === 0 && (
        <Card className="space-y-1 p-5 text-sm">
          <p className="font-medium">Start with a project</p>
          <p className="text-muted-foreground">
            A project holds the topics, brand and formats for one line of content.{' '}
            <Link to="/projects/new" className="text-foreground underline-offset-4 hover:underline">
              Create your first project
            </Link>
            .
          </p>
        </Card>
      )}
    </div>
  )
}

function Stat({ label, value, hint, to }: { label: string; value: number; hint?: string; to: string }) {
  return (
    <Link to={to} className="block">
      <Card className="h-full space-y-1 p-4 transition-colors hover:border-foreground/30">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value.toLocaleString('en-GB')}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </Card>
    </Link>
  )
}
